import React, { useRef, useState, useEffect, useCallback } from "react";
import { ArrowLeft, Maximize2, Minimize2, Play, Pause, Rewind, FastForward, SkipBack, SkipForward, ZoomIn, ZoomOut } from "lucide-react";

const formatTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

/**
 * Plays a single clip or a playlist of clips (for highlights).
 *
 * IMPORTANT: The <video> element is rendered ONCE inside `player-host`. To enter
 * landscape/fullscreen we only change `player-host`'s CSS — we never re-parent
 * the video. Re-parenting causes browsers to mount a fresh <video> node which
 * starts black with no metadata until the source reloads (the exact "screen
 * goes black in landscape" bug). Keeping one node means the video keeps
 * playing right through the fullscreen toggle.
 */
const ClipPlayer = ({ clips, title, subtitle, onBack }) => {
  const list = Array.isArray(clips) ? clips.filter(Boolean) : [];
  const rootRef = useRef(null);
  const videoRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const hideTimer = useRef(null);

  const current = list[index];
  const clipStart = Number(current?.start_time) || 0;
  const clipEnd = Math.max(clipStart + 0.1, Number(current?.end_time) || clipStart + 5);
  const clipDuration = clipEnd - clipStart;
  const relTime = Math.max(0, Math.min(clipDuration, currentTime - clipStart));

  // Totals across the full highlight tape
  const totalDuration = list.reduce((acc, c) => acc + Math.max(0.1, (Number(c?.end_time) || 0) - (Number(c?.start_time) || 0)), 0);
  const elapsedBefore = list.slice(0, index).reduce((acc, c) => acc + Math.max(0.1, (Number(c?.end_time) || 0) - (Number(c?.start_time) || 0)), 0);
  const totalElapsed = elapsedBefore + relTime;

  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (playing) hideTimer.current = setTimeout(() => setShowControls(false), 4000);
  }, [playing]);

  // When the clip changes, seek to its start
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !current) return;
    setVideoError(false);
    const onMeta = () => { try { v.currentTime = clipStart; } catch (e) {} };
    v.addEventListener("loadedmetadata", onMeta);
    if (v.readyState >= 1) { try { v.currentTime = clipStart; } catch (e) {} }
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, [index, current?.video_url, clipStart]);

  // Track currentTime + auto-advance / end
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setCurrentTime(v.currentTime);
      if (v.currentTime >= clipEnd) {
        if (index < list.length - 1) setIndex(i => i + 1);
        else { v.pause(); setPlaying(false); }
      }
    };
    const onEnd = () => {
      if (index < list.length - 1) setIndex(i => i + 1);
      else setPlaying(false);
    };
    const onErr = () => setVideoError(true);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("ended", onEnd);
    v.addEventListener("error", onErr);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("ended", onEnd);
      v.removeEventListener("error", onErr);
    };
  }, [index, list.length, clipEnd]);

  // Auto-play subsequent clips when index advances while playing
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !playing) return;
    const attemptPlay = () => v.play().catch(() => {});
    if (v.readyState >= 2) attemptPlay();
    else v.addEventListener("canplay", attemptPlay, { once: true });
    return () => v.removeEventListener("canplay", attemptPlay);
  }, [index, playing]);

  // Fullscreen events
  useEffect(() => {
    const onFS = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFS);
    const onEsc = (e) => { if (e.key === "Escape" && isFullscreen) setIsFullscreen(false); };
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("fullscreenchange", onFS);
      document.removeEventListener("keydown", onEsc);
    };
  }, [isFullscreen]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
    resetHideTimer();
  };

  const skip = (sec) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(clipStart, Math.min(clipEnd, v.currentTime + sec));
    resetHideTimer();
  };

  const jumpToPrevClip = () => {
    if (index > 0) setIndex(index - 1);
    else if (videoRef.current) { videoRef.current.currentTime = clipStart; }
  };
  const jumpToNextClip = () => {
    if (index < list.length - 1) setIndex(index + 1);
  };

  const onScrub = (pct) => {
    const target = pct * totalDuration;
    let acc = 0;
    for (let i = 0; i < list.length; i++) {
      const d = Math.max(0.1, (Number(list[i]?.end_time) || 0) - (Number(list[i]?.start_time) || 0));
      if (target <= acc + d) {
        const local = target - acc;
        if (i !== index) setIndex(i);
        setTimeout(() => {
          if (videoRef.current) videoRef.current.currentTime = (Number(list[i]?.start_time) || 0) + local;
        }, 50);
        break;
      }
      acc += d;
    }
  };

  const toggleFullscreen = () => {
    const el = rootRef.current;
    if (!el) return;
    if (isFullscreen) {
      if (document.fullscreenElement) {
        try { document.exitFullscreen?.().catch?.(() => {}); } catch { /* */ }
      }
      setIsFullscreen(false);
    } else {
      try { el.requestFullscreen?.().catch?.(() => {}); } catch { /* */ }
      setIsFullscreen(true);
    }
  };

  if (!current) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
        <p>No clips to play</p>
        {onBack && <button onClick={onBack} style={{ marginTop: 16, background: 'var(--cyan)', color: '#000', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600 }}>Back</button>}
      </div>
    );
  }

  const videoSrcMp4 = current.video_url?.endsWith('.webm') ? current.video_url.replace(/\.webm$/, '.mp4') : current.video_url;
  const videoSrcWebm = current.video_url?.endsWith('.webm') ? current.video_url : current.video_url?.replace(/\.mp4$/, '.webm');

  // Sizes the host like the VideoPlayer host. When in fullscreen, position:fixed
  // + 100vw/100vh covers the screen even if the requestFullscreen call failed
  // (iOS PWA wrappers sometimes block it — we still want a working landscape).
  const hostStyle = isFullscreen
    ? { position: 'fixed', inset: 0, zIndex: 9999, background: '#000', display: 'flex', flexDirection: 'column' }
    : { position: 'relative', width: '100%', aspectRatio: '16/9', maxHeight: 'calc(100vh - 320px)', overflow: 'hidden', background: '#000' };

  return (
    <div ref={rootRef} id="clip-player-root" data-testid="clip-player" style={{
      background: '#000',
      minHeight: isFullscreen ? '100vh' : 'auto',
    }}>
      {!isFullscreen && (
        <>
          {/* Top bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: '#0f141d' }}>
            <button data-testid="clip-back-btn" onClick={onBack} style={{
              background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff',
              borderRadius: '50%', width: 40, height: 40, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><ArrowLeft size={18} /></button>
            <div style={{ textAlign: 'center', flex: 1, paddingInline: 16 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#fff', fontFamily: "'Outfit', sans-serif" }}>{title || current.title}</p>
              {subtitle && <p style={{ fontSize: 11, color: '#888' }}>{subtitle}</p>}
              {list.length > 1 && <p style={{ fontSize: 11, color: 'var(--cyan)' }}>Clip {index + 1} of {list.length}</p>}
            </div>
            <button data-testid="clip-fs-btn" onClick={toggleFullscreen} style={{
              background: 'rgba(255,255,255,0.08)', border: 'none', color: '#fff',
              borderRadius: '50%', width: 40, height: 40, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><Maximize2 size={18} /></button>
          </div>
        </>
      )}

      {/* ─── Persistent video host — rendered ONCE. CSS switches between
            embedded and fullscreen modes without ever unmounting <video>. ─── */}
      <div
        data-testid="clip-player-host"
        onMouseMove={resetHideTimer}
        onClick={() => {
          if (isFullscreen) {
            if (!showControls) { setShowControls(true); resetHideTimer(); }
            else {
              if (hideTimer.current) clearTimeout(hideTimer.current);
              setShowControls(false);
            }
          }
        }}
        style={hostStyle}>
        {/* Portrait-only zoom column. Hidden in fullscreen — landscape uses gestures instead. */}
        {!isFullscreen && (
          <div style={{ position: 'absolute', right: 16, top: 12, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 10 }}>
            <button data-testid="clip-zoom-in" onClick={(e) => { e.stopPropagation(); setZoom(z => Math.min(3, z + 0.25)); }} style={{ background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ZoomIn size={16} style={{ color: '#000' }} />
            </button>
            <button data-testid="clip-zoom-out" onClick={(e) => { e.stopPropagation(); setZoom(z => Math.max(1, z - 0.25)); }} style={{ background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ZoomOut size={16} style={{ color: '#000' }} />
            </button>
          </div>
        )}

        <video
          ref={videoRef}
          data-testid="clip-video-element"
          poster={current?.thumbnail_url || undefined}
          preload="metadata"
          playsInline
          style={{
            width: '100%', height: '100%', objectFit: 'contain', display: 'block',
            transform: `scale(${zoom})`, transformOrigin: 'center center',
            transition: 'transform 0.2s ease', background: '#000', flex: 1, minHeight: 0,
          }}
        >
          {videoSrcMp4 && <source src={videoSrcMp4} type="video/mp4" />}
          {videoSrcWebm && videoSrcWebm !== videoSrcMp4 && <source src={videoSrcWebm} type="video/webm" />}
        </video>

        {videoError && (
          <div data-testid="clip-video-error" style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 10,
            background: 'rgba(0,0,0,0.85)', color: '#fff', padding: 20, textAlign: 'center',
          }}>
            <p style={{ fontSize: 15, fontWeight: 600, color: '#ff6b00' }}>Video unavailable</p>
            <p style={{ fontSize: 12, color: '#aaa', maxWidth: 320 }}>This clip's source video couldn't be played.</p>
          </div>
        )}

        {/* ─── Fullscreen controls overlay — sits INSIDE the host (which is now
              the fullscreen surface). Tap-to-toggle is on the host wrapper.  ─── */}
        {isFullscreen && (
          <div
            data-testid="clip-landscape"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              justifyContent: 'space-between', pointerEvents: showControls ? 'auto' : 'none',
              opacity: showControls ? 1 : 0, transition: 'opacity 0.3s ease',
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: 16, background: 'linear-gradient(180deg, rgba(0,0,0,0.7), transparent)' }}>
              <button data-testid="clip-fs-back" onClick={onBack} style={{ ...ctrlStyle, background: 'rgba(0,0,0,0.6)' }}><ArrowLeft size={18} /></button>
              <button data-testid="clip-fs-exit" onClick={toggleFullscreen} style={{ ...ctrlStyle, background: 'rgba(0,0,0,0.6)' }}><Minimize2 size={18} /></button>
            </div>
            <div style={{ padding: 20, background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }}>
              <div data-testid="clip-fs-scrub-row" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 12, color: '#fff', minWidth: 40 }}>{formatTime(totalElapsed)}</span>
                <div
                  data-testid="clip-fs-scrub"
                  onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); onScrub((e.clientX - r.left) / r.width); }}
                  style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.25)', borderRadius: 3, cursor: 'pointer', position: 'relative' }}>
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${totalDuration ? (totalElapsed / totalDuration) * 100 : 0}%`, background: '#ff6b00', borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 12, color: '#fff', minWidth: 40, textAlign: 'right' }}>{formatTime(totalDuration)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 14 }}>
                <button onClick={jumpToPrevClip} style={ctrlStyle}><SkipBack size={20} /></button>
                <button onClick={() => skip(-5)} style={ctrlStyle}><Rewind size={20} /></button>
                <button data-testid="clip-fs-play" onClick={togglePlay} style={{ width: 60, height: 60, borderRadius: '50%', background: '#ff6b00', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {playing ? <Pause size={26} /> : <Play size={26} fill="#fff" style={{ marginLeft: 3 }} />}
                </button>
                <button onClick={() => skip(5)} style={ctrlStyle}><FastForward size={20} /></button>
                <button onClick={jumpToNextClip} style={ctrlStyle}><SkipForward size={20} /></button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ─── Portrait-mode controls below the video ─── */}
      {!isFullscreen && (
        <div style={{ padding: '16px 20px', background: '#0f141d', color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 12, minWidth: 40 }}>{formatTime(totalElapsed)}</span>
            <div
              data-testid="clip-scrub"
              onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); onScrub((e.clientX - r.left) / r.width); }}
              style={{ flex: 1, height: 4, background: '#253040', borderRadius: 2, cursor: 'pointer', position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${totalDuration ? (totalElapsed / totalDuration) * 100 : 0}%`, background: '#ff6b00', borderRadius: 2 }} />
              {list.length > 1 && list.slice(0, -1).map((_, i) => {
                const pre = list.slice(0, i + 1).reduce((a, c) => a + Math.max(0.1, (Number(c?.end_time) || 0) - (Number(c?.start_time) || 0)), 0);
                return <div key={i} style={{ position: 'absolute', top: -4, left: `${(pre / totalDuration) * 100}%`, width: 2, height: 12, background: 'var(--cyan)' }} />;
              })}
            </div>
            <span style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>{formatTime(totalDuration)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <button data-testid="clip-prev" onClick={jumpToPrevClip} disabled={index === 0 && list.length <= 1} style={ctrlStyle}><SkipBack size={18} /></button>
            <button data-testid="clip-rewind" onClick={() => skip(-5)} style={ctrlStyle}><Rewind size={18} /></button>
            <button data-testid="clip-play-btn" onClick={togglePlay} style={{
              width: 56, height: 56, borderRadius: '50%', background: '#ff6b00', border: 'none',
              cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{playing ? <Pause size={24} /> : <Play size={24} fill="#fff" style={{ marginLeft: 3 }} />}</button>
            <button data-testid="clip-forward" onClick={() => skip(5)} style={ctrlStyle}><FastForward size={18} /></button>
            <button data-testid="clip-next" onClick={jumpToNextClip} disabled={index >= list.length - 1} style={ctrlStyle}><SkipForward size={18} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

const ctrlStyle = {
  width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
  border: 'none', color: '#fff', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

export default ClipPlayer;
